import * as React from "react";
import {
  DetailsList,
  DetailsListLayoutMode,
  Selection,
  SelectionMode,
  Spinner,
  Stack,
  Text,
} from "@fluentui/react";
// Helper to map string/enum to SelectionMode
function getSelectionMode(mode) {
  if (mode === "none" || mode === SelectionMode.none) return SelectionMode.none;
  if (mode === "multiple" || mode === SelectionMode.multiple) return SelectionMode.multiple;
  return SelectionMode.single;
}
import { FilterField } from "./FilterField";
import { Pagination } from "@fluentui/react-experiments";
import ColumnFilter, { applyColumnFilters } from "./ColumnFilter";

function filterItems(items, text) {
  const filteredArray = [];
  for (const item of items) {
    let found = false;
    for (const itemKey in item) {
      if (item.hasOwnProperty(itemKey)) {
        const itemValue = item[itemKey];
        if (itemValue && itemValue.toString().toLowerCase().indexOf(text.toString().toLowerCase()) > -1) {
          found = true;
        }
      }
    }
    if (found) filteredArray.push(item);
  }
  return filteredArray;
}

export default class DetailsTable extends React.Component {
  constructor(props) {
    super(props);

    
    this._onFilter = (ev, text = "") => {
      const { itemsPerPage } = this.props;
      const baseItems = this.state.items;
      const { columnFilters } = this.state;
      const sortedColumn = this.state.columns.find((col) => col.isSorted);

      if (text || Object.keys(columnFilters).length > 0) {
        // Apply text filter
        let filteredItems = text ? filterItems(baseItems || [], text) : baseItems;
        
        // Apply column filters
        filteredItems = applyColumnFilters(filteredItems, columnFilters);
        
        const sortedItems = this._copyAndSort(
          filteredItems,
          sortedColumn?.fieldName || "name",
          sortedColumn?.isSortedDescending || false
        );

        const filteredPages = this._splitItemsIntoPages(sortedItems, itemsPerPage);
    
        this.setState({
          filteredItems: sortedItems,
          pages: filteredPages,
          itemsToDisplay: this.props.shouldUsePagination
            ? filteredPages[0]
            : sortedItems,
          currentPage: 0,
          filterText: text,
        });
      } else {
        const pages = this._splitItemsIntoPages(baseItems, itemsPerPage);
        this.setState({
          filteredItems: null, 
          itemsToDisplay: this.props.shouldUsePagination
            ? pages[0]
            : baseItems,
          pages,
          currentPage: 0,
          filterText: "", 
        });
      }
    };

    this._onPageChange = (pageNumber) => {
      const paginatedItems = this.state.filteredItems ? this.state.filteredItems : this.state.items;
      const pages = this.props.shouldUsePagination
        ? this._splitItemsIntoPages(paginatedItems, this.props.itemsPerPage)
        : [paginatedItems];

      this.setState({
        itemsToDisplay: pages[pageNumber] || [],
        currentPage: pageNumber, 
      });
    };

    this._onItemInvoked = (item) => {
      alert(`Item invoked: ${item.name}`);
    };


    // Determine selectionMode from prop (default to single)
    const selectionMode = getSelectionMode(this.props.selectionMode);
    this._selection = (selectionMode !== SelectionMode.none && this.props.selection)
      || (selectionMode !== SelectionMode.none
        ? new Selection({
            onSelectionChanged: () => {
              this.setState({ selectionDetails: this._getSelectionDetails() });
              this.props.selectionHook && this.props.selectionHook(this._selection.getSelection());
            },
            selectionMode,
          })
        : undefined);

    const items = this.props.items || [];
    const pages = this._splitItemsIntoPages(items, this.props.itemsPerPage);
    this.state = {
      currentPage: 0,
      pages: pages,
      items: items,
      filteredItems: null,
      filterText: "",
      columnFilters: {},
      selectionDetails: this.props.getSelectionDetails || this._getSelectionDetails(),
      columns: this._addFilterIconsToColumns(this.props.columns),
      itemsToDisplay: pages[0],
      selectionMode,
    };

    this._onRenderDetailsFooter = (props, defaultRender) => {
      if (!props) {
        return null;
      }
      return (
        <Stack>
          <Stack.Item align="center">
            <Pagination
              styles={{ root: { background: "white" } }}
              selectedPageIndex={this.state.currentPage}
              pageCount={Object.keys(this.state.pages).length}
              onPageChange={this._onPageChange}
              format
              comboBoxAriaLabel={`${10} pages available`}
              previousPageAriaLabel={"previous page"}
              nextPageAriaLabel={"next page"}
              firstPageAriaLabel={"first page"}
              lastPageAriaLabel={"last page"}
              pageAriaLabel={"page"}
              firstPageIconProps={{ iconName: "DoubleChevronLeft" }}
              previousPageIconProps={{ iconName: "ChevronLeft" }}
              nextPageIconProps={{ iconName: "ChevronRight" }}
              lastPageIconProps={{ iconName: "DoubleChevronRight" }}
            />
          </Stack.Item>
        </Stack>
      );
    };
  }

  componentDidUpdate(prevProps) {
    if (prevProps.items !== this.props.items) {
      const { columnFilters, filterText } = this.state;
      const { itemsPerPage } = this.props;
      
      // Apply existing filters to new items
      let filteredItems = this.props.items || [];
      
      // Apply text filter if exists
      if (filterText) {
        filteredItems = filterItems(filteredItems, filterText);
      }
      
      // Apply column filters if any are active
      if (Object.keys(columnFilters).length > 0) {
        filteredItems = applyColumnFilters(filteredItems, columnFilters);
      }
      
      const pages = this._splitItemsIntoPages(filteredItems, itemsPerPage);
      this.setState((prevState) => ({
        items: this.props.items,
        filteredItems: (filterText || Object.keys(columnFilters).length > 0) ? filteredItems : null,
        pages: pages,
        itemsToDisplay: pages[0] || [],
        currentPage: 0,
      }));
    }
    
    if (prevProps.columns !== this.props.columns) {
      this.setState({
        columns: this._addFilterIconsToColumns(this.props.columns),
      });
    }
  }
  
  render() {
    const {
      items,
      selectionDetails,
      columns,
      itemsToDisplay,
      currentPage,
      selectionMode,
    } = this.state;

    const { filteredItems, columnFilters, filterText } = this.state;
    const hasActiveFilters = filterText || Object.keys(columnFilters).length > 0;
    // Use filteredItems when filters are active, otherwise use items (for non-paginated) or itemsToDisplay (for paginated)
    const viewItems = hasActiveFilters 
      ? (this.props.shouldUsePagination ? itemsToDisplay : (filteredItems || []))
      : (this.props.shouldUsePagination ? itemsToDisplay : items);
    const hideSelection = selectionMode === SelectionMode.none;
    // Show "Nothing to display" only when there are no base items AND no filters are active
    const showNothingToDisplay = (!items || items.length === 0) && !hasActiveFilters;
    return (
      <Stack tokens={{ childrenGap: 5 }}>
        {!this.props.hideSelectionDetails && !hideSelection && selectionDetails && <Text>{selectionDetails}</Text>}
        <div className="displayflex" style={{justifyContent:'space-between'}}>
          {this.props.CommandBar && (
            <Stack horizontal styles={{ root: { height: 44 } }}>
              {this.props.CommandBar}
            </Stack>
          )}

          {!this.props.hideFilterField && (
            <FilterField
              message={selectionDetails}
              onChange={this._onFilter}
              items={items}
              Controls={this.props.Controls}
              styles={{ width: "100%" }}
            />
          )}
        </div>
       
        {this.props.loading ? (
          <Spinner label="Loading..." />
        ) : showNothingToDisplay ? (
          <Text>{this.props.emptyStateMessage || "Nothing to display"}</Text>
        ) : (
          <>
            <DetailsList
              items={viewItems || []}
              columns={columns}
              setKey={currentPage}
              layoutMode={DetailsListLayoutMode.justified}
              selection={hideSelection ? undefined : this._selection}
              selectionMode={selectionMode}
              selectionPreservedOnEmptyClick={true}
              ariaLabelForSelectionColumn="Toggle selection"
              ariaLabelForSelectAllCheckbox="Toggle selection for all items"
              checkButtonAriaLabel="Row checkbox"
              onItemInvoked={() => {}}
              onColumnHeaderClick={this._onColumnClick}
              onRenderDetailsFooter={
                this.props.shouldUsePagination && this._onRenderDetailsFooter
              }
              {...this.props.detailsListProps}
            />
            {hasActiveFilters && (!viewItems || viewItems.length === 0) && (
              <Text styles={{ root: { padding: '20px', textAlign: 'center', color: '#666' } }}>
                No data matches the current filters
              </Text>
            )}
          </>
        )}
      </Stack>
    );
  }

  _splitItemsIntoPages(items, itemsPerPage) {
    const amountOfItems = items.length;
    let pages = {};
    let pageNumber = 0;
    for (let i = 0; i < amountOfItems; i++) {
      const item = items[i];
      let amountOfItemsInCurrentPage = (pages[pageNumber] && pages[pageNumber].length) || 0;

      if (!Array.isArray(pages[pageNumber])) {
        pages[pageNumber] = [];
      }
      if (amountOfItemsInCurrentPage <= itemsPerPage) {
        pages[pageNumber].push(item);
        amountOfItemsInCurrentPage += 1;
      } else if (amountOfItemsInCurrentPage > itemsPerPage) {
        pageNumber += 1;
        amountOfItemsInCurrentPage = 0;
        pages[pageNumber] = [];
        pages[pageNumber].push(item);
        amountOfItemsInCurrentPage += 1;
      }
    }
    return pages;
  }

  _onColumnClick = (ev, column) => {
    const { columns, filteredItems, filterText, columnFilters } = this.state;
    const newColumns = columns.map((col) => ({
      ...col,
      isSorted: col.key === column.key,
      isSortedDescending: col.key === column.key
        ? !col.isSortedDescending
        : col.isSortedDescending,
    }));
  
    const sortedColumn = newColumns.find((col) => col.key === column.key);
    const hasFilters = filterText || Object.keys(columnFilters).length > 0;
    const sourceItems = hasFilters ? filteredItems || [] : this.state.items;
    const sortedItems = this._copyAndSort(
        sourceItems,
        column.fieldName,
        sortedColumn.isSortedDescending
    );

    const pagedItems = this._splitItemsIntoPages(sortedItems, this.props.itemsPerPage);
  
    this.setState({
      columns: newColumns,
      items: hasFilters ? this.state.items : sortedItems,
      filteredItems: hasFilters ? sortedItems : null,
      pages: pagedItems,
      itemsToDisplay: this.props.shouldUsePagination ? pagedItems[0] : sortedItems,
      currentPage: 0,
      announcedMessage: `${column.name} is sorted ${sortedColumn.isSortedDescending ? "descending" : "ascending"}`,
    });
  };

  _copyAndSort(items, columnKey, isSortedDescending) {
    return items.slice().sort((a, b) => {
      const aValue = a[columnKey] ?? ""; 
      const bValue = b[columnKey] ?? "";
  
      if (aValue < bValue) return isSortedDescending ? 1 : -1;
      if (aValue > bValue) return isSortedDescending ? -1 : 1;
      return 0;
    });
  }

  _addFilterIconsToColumns = (columns) => {
    if (!columns) return columns;

    if (this.props.hideColumnFilters) {
      return columns;
    }
    
    return columns.map((column) => {
      // Don't add filter or sort if column has no name or fieldName
      if (!column.name && !column.fieldName) {
        return {
          ...column,
          isSorted: false,
          isSortedDescending: false,
        };
      }
      
      // Don't allow sorting on columns without fieldName
      if (!column.fieldName) {
        return {
          ...column,
          isSorted: false,
          isSortedDescending: false,
          onRenderHeader: (props) => this._renderColumnHeaderWithFilter(props, column),
        };
      }
      
      return {
        ...column,
        onRenderHeader: (props) => this._renderColumnHeaderWithFilter(props, column),
      };
    });
  };

  _renderColumnHeaderWithFilter = (props, column) => {
    const { columnFilters } = this.state;
    const columnKey = column.fieldName || column.key;
    const currentFilter = columnFilters[columnKey];

    return (
      <Stack
        horizontal
        verticalAlign="center"
        styles={{ root: { width: "100%" } }}
      >
        <Text
          styles={{
            root: {
              fontWeight: 600,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            },
          }}
        >
          {column.name}
        </Text>
        <ColumnFilter
          column={column}
          currentFilter={currentFilter}
          onFilterChange={this._onColumnFilterChange}
        />
      </Stack>
    );
  };

  _onColumnFilterChange = (filterConfig) => {
    const { columnFilters, items, filterText } = this.state;
    const { itemsPerPage } = this.props;

    const newColumnFilters = {
      ...columnFilters,
      [filterConfig.columnKey]: filterConfig.isActive ? filterConfig : undefined,
    };

    // Clean up undefined filters
    Object.keys(newColumnFilters).forEach((key) => {
      if (!newColumnFilters[key]) {
        delete newColumnFilters[key];
      }
    });

    // Apply text filter first if exists
    let filteredByText = filterText ? filterItems(items, filterText) : items;

    // Apply column filters
    const filteredByColumns = applyColumnFilters(filteredByText, newColumnFilters);

    // Sort the filtered items
    const sortedColumn = this.state.columns.find((col) => col.isSorted);
    const sortedItems = this._copyAndSort(
      filteredByColumns,
      sortedColumn?.fieldName || "name",
      sortedColumn?.isSortedDescending || false
    );

    const pages = this._splitItemsIntoPages(sortedItems, itemsPerPage);

    this.setState({
      columnFilters: newColumnFilters,
      filteredItems: sortedItems,
      pages,
      itemsToDisplay: this.props.shouldUsePagination ? pages[0] : sortedItems,
      currentPage: 0,
    });
  };

  _getSelectionDetails() {
    const selectionCount = this._selection?.getSelectedCount();
    switch (selectionCount) {
      case 0:
        return "No items selected";
      case 1:
        return (
          <>
            1 item selected:{" "}
            <Text styles={{ root: { fontWeight: "bold" } }}>
              {this._selection.getSelection()[0][this.props.mainColumn]}
            </Text>
          </>
        );
      default:
        return `${selectionCount} items selected`;
    }
  }
}

DetailsTable.defaultProps = {
  itemsPerPage: 10,
  shouldUsePagination: true,
  selectionMode: "single", // default
  hideColumnFilters: false,
};
